import { useEffect, useState, useContext } from "react";
import {
  GetSignedAgreementsQuery,
  useGetAgreementManyLazyQuery,
  useGetClientGroupManyLazyQuery,
  useUpdateSignedAgreementMutation,
} from "src/generated/graphql";
import AgreementsListModal from "./AgreementsList";
import AgreementGroups from "./AgreementGroups";
import AgreementOverview from "./AgreementOverview";
import { ModalStages } from "src/utils/constants";
import SignPolicies from "./SignPolicies";
import DeviceUnlock from "./DeviceUnlock";
import PaymentMethod from "./PaymentMethod";
import Confirmation from "./Confirmation";
import { useModals } from "@mantine/modals";
import { AppContext } from "src/api/AppContext";
import { ISelectedPayment } from "src/components/PaymentSelection";
import { UnsavedMethod } from "src/api/context/CatalogContext";
import { useMantineTheme } from "@mantine/core";
import PayrocCardInput from "../../../Payments/payrocHostedCardInput";

interface ClientOverProps {
  clientId: string;
  agreementData: GetSignedAgreementsQuery["signedAgreementMany"];
  onRefetch: () => void;
}

const CreateSignedAgreement = ({
  clientId,
  agreementData,
  onRefetch,
}: ClientOverProps) => {
  const theme = useMantineTheme();
  const modals = useModals();
  const { currentLocation } = useContext(AppContext);
  const [currentModalStage, setCurrentModalStage] = useState<ModalStages>(
    ModalStages.AGREEMENTS
  );
  const [isNewAgreement, setIsNewAgreement] = useState<boolean>(true);
  const [selectedAgreementId, setSelectedAgreementId] = useState<string | null>(
    null
  );
  const [signedAgreementId, setSignedAgreementId] = useState<string | null>(
    null
  );
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<ISelectedPayment | null>(null);

  const [getClientGroups, { data: clientGroups }] =
    useGetClientGroupManyLazyQuery();

  const [getAgreements, { data: agreements }] = useGetAgreementManyLazyQuery();

  const [updateSignedAgreement, { data: updateData, loading }] =
    useUpdateSignedAgreementMutation();

  useEffect(() => {
    if (clientId) {
      getAgreements({
        variables: {
          locationIds: [{ id: currentLocation?.id! }],
        },
      });
      getClientGroups({
        variables: {
          id: clientId,
        },
        fetchPolicy: "network-only",
      });
    }
  }, [clientId]);

  const setSelPaymentMethod = (selected: ISelectedPayment | null) => {
    setCurrentModalStage(ModalStages.CONFIRMATION);
    setSelectedPaymentMethod(selected);
  };

  const handleUnsavedMethod = (method: UnsavedMethod) => {
    modals.openModal({
      modalId: "secureCardInputModal",
      closeOnClickOutside: false,
      title: "Secure Card Input",
      children: (
        <PayrocCardInput
          clientId={clientId || ""}
          onComplete={(newPaymentMethod) => {
            updateSignedAgreement({
              variables: {
                id: signedAgreementId || "",
                data: {
                  paymentMethod: { id: newPaymentMethod.id },
                },
              },
              onCompleted: (data) => {
                modals.closeModal("secureCardInputModal");
                setCurrentModalStage(ModalStages.CONFIRMATION);
              },
            });
          }}
        />
      ),
      styles: {
        modal: {
          backgroundColor: "rgba(0,0,0,0) !important",
        },
        header: {
          backgroundColor: theme.colors.primary[6],
          color: theme.colors.primaryTextColor[6],
          borderTopLeftRadius: 4,
          borderTopRightRadius: 4,
        },
        body: {
          color: "#222222 !important",
          backgroundColor: "#f0f0f0",
          borderBottomLeftRadius: 4,
          borderBottomRightRadius: 4,
        },
      },
    });
  };

  const handleModalClose = () => {
    onRefetch();
    modals.closeAll();
  };

  const selectedClientGroup =
    selectedGroupId &&
    clientGroups?.clientGroupMany?.find((e) => e.id === selectedGroupId);

  const modalsList = {
    [ModalStages.AGREEMENTS]: (
      <AgreementsListModal
        agreementsList={agreements?.agreementMany || []}
        existingAgreements={agreementData}
        clientGroups={clientGroups?.clientGroupMany || []}
        setSelectedAgreementId={setSelectedAgreementId}
        setCurrentModalStage={setCurrentModalStage}
        setIsNewAgreement={setIsNewAgreement}
        handleModalClose={handleModalClose}
      />
    ),
    [ModalStages.AGREEMENTGROUPS]: (
      <AgreementGroups
        clientGroups={clientGroups?.clientGroupMany || []}
        clientId={clientId}
        selectedAgreement={
          (selectedAgreementId &&
            agreements?.agreementMany?.find(
              (e) => e.id === selectedAgreementId
            )) ||
          undefined
        }
        setCurrentModalStage={setCurrentModalStage}
        setSelectedGroupId={setSelectedGroupId}
        handleModalClose={handleModalClose}
      />
    ),
    [ModalStages.AGREEMENTOVERVIEW]: (
      <AgreementOverview
        agreement={
          (selectedAgreementId &&
            agreements?.agreementMany?.find(
              (e) => e.id === selectedAgreementId
            )) ||
          undefined
        }
        selectedGroup={{ ...selectedClientGroup }}
        setCurrentModalStage={setCurrentModalStage}
        isNewAgreement={isNewAgreement}
        clientId={clientId}
        handleModalClose={handleModalClose}
        setSignedAgreementId={setSignedAgreementId}
      />
    ),
    [ModalStages.SIGNPOLICIES]: (
      <SignPolicies
        setCurrentModalStage={setCurrentModalStage}
        clientId={clientId}
        selectedAgreementId={selectedAgreementId || ""}
      />
    ),
    [ModalStages.DEVICEUNLOCK]: (
      <DeviceUnlock
        setCurrentModalStage={setCurrentModalStage}
        handleModalClose={handleModalClose}
      />
    ),
    [ModalStages.PAYMENTMETHOD]: (
      <PaymentMethod
        submitUnsavedMethod={handleUnsavedMethod}
        setSelectedPaymentMethod={setSelPaymentMethod}
        handleModalClose={handleModalClose}
        clientId={clientId}
        permittedMethods={[UnsavedMethod.Card]}
      />
    ),
    [ModalStages.CONFIRMATION]: (
      <Confirmation
        clientId={clientId}
        selectedAgreementId={selectedAgreementId || ""}
        paymentMethod={selectedPaymentMethod}
        handleModalClose={handleModalClose}
      />
    ),
  };

  return <>{modalsList[currentModalStage]}</>;
};

export default CreateSignedAgreement;
